// FAQ copy per page. The visible FAQ blocks and the FAQPage JSON-LD are both built
// from these arrays, so they can never drift apart.
export const FAQS = {
  home: {
    eyebrow: 'Questions',
    title: 'Questions security teams ask us',
    items: [
      {
        q: 'What does DIGU do?',
        a: 'DIGU secures AI coding agents inside engineering organizations. We assess how your teams use Claude Code and other agents against 32 controls, implement those controls in your environment, and hand you the evidence your auditors ask for. DIGU is Certified in the Claude Partner Network and is an Okta partner, with certified Claude engineers on every project.',
      },
      {
        q: 'Do you only work with Claude?',
        a: 'No. We start with Claude, because DIGU is Certified in the Claude Partner Network. The same 32 controls cover GitHub Copilot, Cursor and the other coding agents your engineers use. The risks are the same; only the settings that close them differ by platform.',
      },
      {
        q: 'What is the biggest security risk with AI coding agents?',
        a: 'Configuration, not the model. In April 2026 an agent deleted a production database using an unscoped token it found in a file. In August 2026 researchers showed coding agents leaking CI credentials through text in a GitHub issue. Scoped agent identity, gated workflows and attributable logs close both paths.',
      },
      {
        q: 'How long does it take?',
        a: 'The assessment takes one week and makes no changes to your environment. Security Hardening then takes three to four weeks to put the controls in place and produce the evidence pack. Every engagement is a fixed fee against a fixed list of deliverables.',
      },
      {
        q: 'How much does it cost?',
        a: 'Every engagement is one fixed fee against a fixed list of deliverables, never an hourly rate. Ask for the price list and the full fee schedule arrives by email, with no call in between. The assessment fee is credited in full against any engagement signed within thirty days.',
      },
      {
        q: 'Can DIGU make us compliant?',
        a: 'No one can certify you compliant for using AI. Certification is the work of an accredited auditor. We implement controls mapped to OWASP, the NIST AI Risk Management Framework and ISO/IEC 42001, and produce the evidence your auditor expects, so the answer already exists when they ask.',
      },
    ],
  },

  services: {
    eyebrow: 'Questions',
    title: 'Questions about working with us',
    items: [
      {
        q: 'Where should we start?',
        a: 'With the one week assessment. We score your organization against 32 controls using your own data, rank the risks, and give you a ninety day plan with a price on every line. No changes are made to your environment during the week.',
      },
      {
        q: 'Do you work in our environment or yours?',
        a: 'Yours. Everything we build runs in your cloud, your repositories and your monitoring, using the identity provider and tools you already own. We hold none of your data, we do not sell software, and we do not resell licenses.',
      },
      {
        q: 'Can we buy Security Hardening and Claude Code Rollout together?',
        a: 'Yes, and they become one engagement at one fee. Both configure settings, hooks and telemetry, so bought together you pay for that shared work once rather than twice.',
      },
      {
        q: 'Do you secure GitHub Copilot and Cursor as well as Claude?',
        a: "Yes. Our 32 controls are written against the risk, not the vendor, so they apply to every AI coding agent in your estate. We implement each control with the platform's own settings, then prove it with the same evidence.",
      },
      {
        q: 'What do you not do?',
        a: 'We do not certify compliance, run a security operations center, take 24 hour response duty, perform penetration testing, build your SIEM or replace your identity platform. When you need one of those, we will say so and point you to someone who does it well.',
      },
    ],
  },

  roadmap: {
    eyebrow: 'Questions',
    title: 'Questions about the maturity model',
    items: [
      {
        q: 'What is an AI agent security maturity model?',
        a: 'It is a way to measure how safely your organization uses AI agents. Ours has five levels, from 0 to 4, scored separately across five control domains. Each level is described by what it lets you safely do next, not by how locked down you are.',
      },
      {
        q: 'How many controls does each level need?',
        a: 'Level 1 needs 8 of the 32 controls in the DIGU AI Security Baseline. Level 2 needs 20. Level 3 needs all 32. Level 4 keeps all 32 and adds five practices, such as automated control testing and scheduled red teaming.',
      },
      {
        q: 'What level should our organization target?',
        a: 'Most software companies should target level 2. Regulated organizations, and those holding SOC 2 or ISO 27001, should target level 3 across all five domains. Level 4 is only worth reaching where agents provision infrastructure or run in your pipelines.',
      },
      {
        q: 'How is maturity scored?',
        a: 'On evidence. A control counts only when its evidence exists. We report two numbers: the average across the five domains, and the floor, which is your lowest domain. Risk follows the floor, so the floor is the number to act on.',
      },
      {
        q: 'How fast can we move up a level?',
        a: 'One level at a time. A two level jump in a single quarter usually means the controls went in without the adoption. Never leave a domain at level 0 once an AI agent has production access.',
      },
    ],
  },

  baseline: {
    eyebrow: 'Questions',
    title: 'Questions about the baseline',
    items: [
      {
        q: 'What is the DIGU AI Security Baseline?',
        a: 'It is a published set of 32 security controls for AI coding agents, in five domains: identity and privilege, permissions and execution boundaries, untrusted content, connectivity and supply chain, and observability and governance. Each control states what it is, how it is implemented, and the evidence that proves it.',
      },
      {
        q: 'Which frameworks does the baseline map to?',
        a: "Every control maps to the OWASP Top 10 for Agentic Applications, Anthropic's published security guidance, the NIST AI Risk Management Framework and ISO/IEC 42001. The baseline is also aligned to the Blueprint Alliance reference architecture, so the work lands inside programs you already run.",
      },
      {
        q: 'Does it cover MCP servers and connectors?',
        a: 'Yes. The connectivity and supply chain domain has 5 controls covering what an agent can connect to and send data to. Connections are allowlisted, and the evidence includes your connector allowlist and egress rules.',
      },
      {
        q: 'Does the baseline apply to GitHub Copilot and Cursor?',
        a: 'Yes. Each control is written against a risk, such as an agent holding too much access or trusting outside input, not against one vendor. The implementation changes by platform. The control and its evidence do not.',
      },
      {
        q: 'Can we use the baseline without hiring DIGU?',
        a: 'Yes. It is published so a security team can read it, argue with it and score itself for free. What we add is the evidence, the implementation in your environment, and someone to keep it current as the tools change.',
      },
    ],
  },
};
